import { Injectable } from '@angular/core';
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {BehaviorSubject, catchError, finalize, map, Observable, share, throwError} from "rxjs";
import {LoginDto} from "../models/login-dto";
import {
  IDENTITY_TOKEN,
  OIDC_DEFAULT_HEADERS,
  PASSWORD_FLOW_DATA,
  REFRESH_TOKEN_FLOW_DATA
} from "../constants/identity.constants";
import {SessionData} from "../models/session-data";
import {JwtHelperService} from "@auth0/angular-jwt";
import {StorageService} from "./storage.service";
import {Router} from "@angular/router";
import {MessageService} from "primeng/api";
import {DataService, mayPassOnRetry} from "./data.service";
import {SESSION_REFRESH_FAILED, SessionRefreshError} from "./session-refresh-error";
import {HttpErrorResponse, HttpParams} from "@angular/common/http";
import {HttpParameterEncoder} from "../utils/http-parameter-code";

/**
 * Identity refused the password grant: OpenIddict answers 400 with `error: invalid_grant`
 * (Identity TokenController — unknown email, wrong password, unconfirmed email).
 */
export function isPasswordRejected(error: unknown): error is HttpErrorResponse {
  return error instanceof HttpErrorResponse && error.status === 400 && error.error?.error === 'invalid_grant';
}

/**
 * Identity TokenController's `error_description` for a correct password on an unconfirmed email — must match the
 * literal in apps/identity/src/Paratype.Apps.Identity/Controllers/TokenController.cs (line 74, the
 * IsEmailConfirmedAsync branch). Compared by equality: if the text changes there, the login page silently falls back
 * to the generic "Invalid email or password" message.
 */
export const UNCONFIRMED_EMAIL_DESCRIPTION = 'The user email address not confirmed.';

/** Ключ сессии в localStorage (PAR-12762). */
const USER_SESSION_KEY = 'userSession';

@Injectable({
  providedIn: 'root'
})
export class IdentityService {

  public userSession: BehaviorSubject<any> = new BehaviorSubject(null);
  public isAuth: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public isAdminRole: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public roleHasAccess: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public catalogManagementAccess: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public storeManagementAccess: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public ordersManagementAccess: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  // PAR-12507 — matches OrderAssemblyController's [Authorize(Roles = "Admin,SaleManager")].
  public orderAssemblyAccess: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  // The token refresh in flight in this tab: parallel 401s share one request (PAR-13011).
  private refresh$: Observable<void> | null = null;

  constructor(private dataService: DataService, private storageService: StorageService, private router: Router,
              private messageService: MessageService) {
    const currentSession = this.storedSession();
    if(currentSession) {
      this.adopt(currentSession);
    }

    // PAR-12762: сессия общая для вкладок (localStorage). Выход в соседней вкладке выводит и эту, её обновление токена
    // эта подхватывает. Вкладка без сессии на чужой вход не реагирует — как сайт, который подменяет лишь сессию в памяти
    // и никуда не уводит.
    this.storageService.changes(USER_SESSION_KEY)
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.followOtherTab());
  }

  // A rejected password grant is shown by the login page above the fields, so it bypasses the common toast and
  // reaches the page as the original HttpErrorResponse (isPasswordRejected). Token refresh has its own rule (refreshToken).
  // PAR-12762: вход без доступа к панели (roleHasAccess = false) в общее хранилище не пишется и чужую сессию не трогает —
  // иначе запись и следующее удаление выводили бы соседние вкладки. Такой вход сбрасывает только память этой вкладки.
  public login(model: LoginDto): Observable<any> {
    return this.identityRequest(IDENTITY_TOKEN, {...PASSWORD_FLOW_DATA, ...model}, isPasswordRejected)
      .pipe(
        map(_ => {
          if (this.hasPanelAccess()) {
            this.saveSession();
            this.setGuardSubjects(true);
          } else {
            this.userSession.next(null);
            this.setGuardSubjects(false);
          }
          return this.userSession.getValue();
        })
      );
  }

  /**
   * One refresh request per tab: parallel callers share it. The split is the one the token request retry uses
   * (`mayPassOnRetry`): a 4xx from Identity (revoked or invalid refresh token, invalid client) ends the session — storage
   * is cleared, this tab goes to /login, the other tabs follow through the `storage` event; no response, a 5xx or a 429
   * (rate limit, PAR-13076) leaves the session as it is and shows one message. Either way the subscribers get a `SessionRefreshError`, never the
   * original HttpErrorResponse (PAR-13011).
   */
  public refreshToken(): Observable<void> {
    return this.refresh$ ??= this.identityRequest(IDENTITY_TOKEN, {
      ...REFRESH_TOKEN_FLOW_DATA,
      refresh_token: this.userSession.getValue().refresh_token
    }, () => true).pipe(
      map(() => this.adoptRefreshedSession()),
      catchError(error => this.onRefreshFailed(error)),
      finalize(() => (this.refresh$ = null)),
      share()
    );
  }

  // The roles come from the new token: no Control Panel access any more ends the session. Checked before the write,
  // so the other tabs never adopt a session without access.
  private adoptRefreshedSession(): void {
    this.endSessionWithoutPanelAccess();
    this.saveSession();
    this.setGuardSubjects(true);
  }

  private endSessionWithoutPanelAccess(): void {
    if (this.hasPanelAccess()) return;
    this.logout();
    throw new SessionRefreshError();
  }

  private onRefreshFailed(error: unknown): Observable<never> {
    if (error instanceof SessionRefreshError) {
      return throwError(() => error);
    }
    // Identity answered (4xx: revoked or invalid refresh token, invalid client) — the session is over. No answer or a
    // 5xx (what the token request retry already covered) — the session stays, the user gets the one message.
    // PAR-13076: 429 is Identity's rate limit (Retry-After), not a verdict on the refresh token: the session stays too.
    // Handled here, not in mayPassOnRetry — that predicate also decides the retry of reads.
    const rateLimited = error instanceof HttpErrorResponse && error.status === 429;
    const rejectedByIdentity = !rateLimited && !mayPassOnRetry(error);
    if (rejectedByIdentity) {
      this.logout();
    } else {
      this.messageService.add({severity: 'error', summary: 'Error', detail: SESSION_REFRESH_FAILED});
    }
    return throwError(() => new SessionRefreshError());
  }

  signIn(path: string, body: object, passThrough?: (error: HttpErrorResponse) => boolean): Observable<SessionData> {
    const params: HttpParams = new HttpParams({
      fromObject: {...body}, encoder: new HttpParameterEncoder()
    });
    return this.dataService.postWithHeaders<SessionData>(path, params, OIDC_DEFAULT_HEADERS, passThrough)
  }

  private identityRequest(path: string, body: object, passThrough?: (error: HttpErrorResponse) => boolean) {
    return this.signIn(path, body, passThrough)
      .pipe(
        map((data: SessionData) => {
          data.log_date = Date.now() / 1000 || 0;
          const helper = new JwtHelperService();
          const decodedToken = helper.decodeToken(data.access_token);
          data.email = decodedToken['username'];
          const role = decodedToken['role'];
          if (role instanceof(Array)) {
            data.role = decodedToken['role'].join(';');
          } else {
            data.role = decodedToken['role'];
          }
          this.userSession.next({...this.userSession.getValue(), ...data});
        })
      );
  }

  private saveSession(): void {
    this.storageService.saveStorageObject(this.userSession.value, USER_SESSION_KEY);
  }

  public logout(): void {
    this.storageService.removeStorageObject(USER_SESSION_KEY);
    this.leave();
  }

  private followOtherTab(): void {
    if (!this.userSession.getValue()) return;
    const stored = this.storedSession();
    if (stored) {
      this.adopt(stored);
    } else {
      this.leave();
    }
  }

  // Сессия — только объект с токеном: иное значение в localStorage (оно переживает перезапуск) давало бы isAuth без токена.
  private storedSession(): SessionData | null {
    const stored = this.storageService.storageObject<SessionData>(USER_SESSION_KEY);
    return typeof stored?.access_token === 'string' ? stored : null;
  }

  private adopt(session: SessionData): void {
    this.userSession.next(session);
    this.setGuardSubjects(true);
  }

  private leave(): void {
    this.userSession.next(null);
    this.setGuardSubjects(false);
    this.router.navigate(['/login']);
  }

  public get authToken(): string {
    return this.userSession.getValue() && this.userSession.getValue().access_token;
  }

  public isAdmin(): boolean {
    return this.getRoleCheck('ADMIN');
  }

  public isManager(): boolean {
    return this.getRoleCheck('MANAGER');
  }

  public isExternalManager(): boolean {
    return this.getRoleCheck('EXTERNALMANAGER');
  }

  public isSaleManager(): boolean {
    return this.getRoleCheck('SALEMANAGER');
  }

  // PAR-12507 — isCartManager() removed: CartManager is a SITE role (cart work in the Store SPA) and
  // has no Control Panel surface at all. Do not re-add a CP role check for it without an owner decision.

  // PAR-12508 — currentUserId removed. It existed only to fill CreateDraftOrderCommand.ManagerId,
  // which the server now derives from the token itself. Reading `sub` here was never a control
  // anyway: the endpoint accepted whatever value was sent. Do not re-add a client-side actor id to
  // feed a server-side attribution field.

  private getRoleCheck(role: string) {
    const session = this.userSession.getValue() as SessionData | null;
    if (session) {
      const roles = session.role?.split(';').map(r => r.toUpperCase());
      if (roles && roles.length > 0) {
        return roles.includes(role);
      }
    }
    return false;
  }

  private hasPanelAccess(): boolean {
    return this.isAdmin() || this.isManager() || this.isExternalManager() || this.isSaleManager();
  }

  private setGuardSubjects(isAuth: boolean) {
    this.isAuth.next(isAuth);
    this.roleHasAccess.next(this.hasPanelAccess());
    this.catalogManagementAccess.next(this.isAdmin() || this.isManager());
    this.storeManagementAccess.next(this.isAdmin() || this.isManager() || this.isExternalManager());
    this.ordersManagementAccess.next(this.isAdmin() || this.isManager() || this.isSaleManager());
    this.orderAssemblyAccess.next(this.isAdmin() || this.isSaleManager());
    this.isAdminRole.next(this.isAdmin());
  }
}
